import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { api } from "@/lib/api";

/** Convert URL-safe base64 (VAPID format) into the Uint8Array Web Push expects. */
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export default function NotificationToggle({ stationId, label = "Notify me of new shows" }) {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "default"));

  useEffect(() => {
    if (!pushSupported()) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const key = `fliboe_push_${stationId}`;
          setSubscribed(localStorage.getItem(key) === sub.endpoint);
        }
      } catch (_) {}
    })();
  }, [stationId]);

  if (!pushSupported()) return null;

  const subscribe = async () => {
    setBusy(true);
    try {
      let permission = perm;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
        setPerm(permission);
      }
      if (permission !== "granted") { setBusy(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        station_id: stationId,
        listener_id: localStorage.getItem("fliboe_listener_id") || null,
      });
      localStorage.setItem(`fliboe_push_${stationId}`, json.endpoint);
      setSubscribed(true);
    } catch (e) {
      console.warn("[push] subscribe failed", e);
    }
    setBusy(false);
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint, station_id: stationId }).catch(() => {});
        // Only fully unsubscribe at the browser level if user has no other station subs.
        // Simplest: leave the browser subscription; just untag this station server-side.
      }
      localStorage.removeItem(`fliboe_push_${stationId}`);
      setSubscribed(false);
    } catch (_) {}
    setBusy(false);
  };

  if (perm === "denied") {
    return (
      <button
        disabled
        className="fl-btn ghost !py-2 !px-3 text-xs opacity-60 cursor-not-allowed"
        title="Notifications are blocked for this site in your browser settings."
        data-testid="notify-blocked"
      >
        <BellOff className="w-4 h-4" /> Notifications blocked
      </button>
    );
  }

  return subscribed ? (
    <button
      onClick={unsubscribe}
      disabled={busy}
      className="fl-btn ghost !py-2 !px-3 text-xs"
      data-testid="notify-unsubscribe"
      title="Stop receiving push alerts for this station"
    >
      <BellRing className="w-4 h-4 text-emerald-500" /> Notifications on
    </button>
  ) : (
    <button
      onClick={subscribe}
      disabled={busy}
      className="fl-btn ghost !py-2 !px-3 text-xs"
      data-testid="notify-subscribe"
    >
      <Bell className="w-4 h-4" /> {busy ? "Enabling…" : label}
    </button>
  );
}
